import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";

import {
  doc,
  getDoc
} from "firebase/firestore";

import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";

import { db } from "../utils/firebase";
import { useAuth } from "./AuthContext";
import { TokenContext } from "./tokenContext";
import { logout } from "../utils/firebase";
import GlobalNavDrawer from "./GlobalNavDrawer";
import {
  getCoachDisplayName,
  getDirectoryCoach,
  getPublicContactFields,
  listDirectoryCoaches
} from "../services/coachDirectoryService";
import {
  acceptRelationship,
  declineRelationship,
  endRelationship,
  getActiveCandidateRelationship,
  getRelationship,
  getRelationshipStatusLabel,
  getRelationshipViewState,
  migrateLegacyRelationships,
  RELATIONSHIP_VIEW_STATE,
  requestCoach
} from "../services/coachRelationshipService";
import {
  redirectToJobBankLogin,
  useJobBankRole
} from "./jobBankRole";

import "./PublicProfiles.css";

function clean(value) {
  return String(value || "").trim();
}

function getInitials(name = "") {
  const parts = clean(name).split(/\s+/).filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return "CC";
}

function getLocation(profile = {}) {
  return [profile.city, profile.province].filter(Boolean).join(", ");
}

function getSpecialties(profile = {}) {
  const raw = profile.specialties || profile.primaryOccupation || "";

  return String(raw)
    .split(/[,;|]/)
    .map(item => item.trim())
    .filter(Boolean)
    .slice(0, 4);
}

function getBio(profile = {}) {
  return clean(profile.coachBio || profile.bio);
}

function Drawer({ balance }) {
  return createPortal(
    <GlobalNavDrawer
      hideCurrent
      links={[
        { to: "/purchase", label: `Tokens: ${balance}` },
        { to: "/jobbank", label: "Job Bank Home" },
        { to: "/jobbankjobs", label: "Jobs" },
        { to: "/jobcarveouts", label: "Job Carve-Outs" },
        { to: "/events", label: "Hiring Events" },
        { to: "/profile", label: "My Profile" },
        { to: "/admin", label: "Admin" },
        {
          to: "/login",
          label: "Logout",
          onClick: async () => {
            await logout();
          }
        }
      ]}
    />,
    document.body
  );
}

function CoachDirectoryCard({ coach, onClick }) {
  const name = getCoachDisplayName(coach);
  const location = getLocation(coach);
  const specialties = getSpecialties(coach);
  const bio = getBio(coach);

  return (
    <article
      className="coachDirectoryCard"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
    >
      <div className="coachAvatar">
        {coach.photoURL ? (
          <img src={coach.photoURL} alt="" />
        ) : (
          <span>{getInitials(name)}</span>
        )}
      </div>

      <div className="coachCardIdentity">
        <h2>{name}</h2>
        <p>Career Coach</p>
      </div>

      {bio && <p className="coachCardBio">{bio}</p>}

      <div className="coachCardMeta">
        {location && <span className="coachLocation">📍 {location}</span>}

        {specialties.length > 0 && (
          <div className="coachBadgeRow">
            {specialties.map(item => (
              <span className="coachBadge" key={`${coach.id}-${item}`}>
                {item}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="coachCardFooter">
        <span>Available for new clients</span>
        <span aria-hidden="true">→</span>
      </div>
    </article>
  );
}

function RelationshipActions({
  viewState,
  statusLabel,
  saving,
  requestBlocked,
  onRequest,
  onAccept,
  onDecline,
  onEnd,
  onViewRelationship
}) {
  switch (viewState) {
    case RELATIONSHIP_VIEW_STATE.COACH_INVITED:
      return (
        <div className="coachProfileActions">
          <button onClick={onAccept} disabled={saving}>Accept</button>
          <button className="secondaryButton" onClick={onDecline} disabled={saving}>
            Decline
          </button>
        </div>
      );

    case RELATIONSHIP_VIEW_STATE.CANDIDATE_REQUESTED:
      return (
        <div className="coachProfileActions">
          <button disabled>Request Pending</button>
        </div>
      );

    case RELATIONSHIP_VIEW_STATE.ACTIVE:
      return (
        <div className="coachProfileActions">
          <button onClick={onViewRelationship}>View Relationship</button>
          <button className="secondaryButton" onClick={onEnd} disabled={saving}>
            End Relationship
          </button>
        </div>
      );

    case RELATIONSHIP_VIEW_STATE.DECLINED:
      return (
        <div className="coachProfileActions">
          <button disabled>Declined</button>
        </div>
      );

    case RELATIONSHIP_VIEW_STATE.ENDED:
      return (
        <div className="coachProfileActions">
          <button disabled>Relationship Ended</button>
        </div>
      );

    case RELATIONSHIP_VIEW_STATE.NONE:
    default:
      return (
        <div className="coachProfileActions">
          <button onClick={onRequest} disabled={saving || requestBlocked}>
            Request Coaching
          </button>
        </div>
      );
  }
}

function CoachProfilePanel({
  coach,
  relationship,
  viewState,
  statusLabel,
  saving,
  requestBlocked,
  onBack,
  onRequest,
  onAccept,
  onDecline,
  onEnd,
  onViewRelationship
}) {
  const name = getCoachDisplayName(coach);
  const location = getLocation(coach);
  const specialties = getSpecialties(coach);
  const bio = getBio(coach);
  const contact = getPublicContactFields(coach);

  return (
    <>
      <button className="coachBackButton" onClick={onBack}>
        ← Back to Coaches
      </button>

      <div className="coachProfileLayout">
        <section className="coachProfileHero">
          <div className="coachAvatar coachAvatarLarge">
            {coach.photoURL ? (
              <img src={coach.photoURL} alt="" />
            ) : (
              <span>{getInitials(name)}</span>
            )}
          </div>

          <div>
            <h1>{name}</h1>
            <p className="coachProfileRole">Career Coach</p>
            <p className="coachRelationshipStatus">{statusLabel}</p>

            {location && (
              <p className="coachProfileLocation">📍 {location}</p>
            )}

            {specialties.length > 0 && (
              <div className="coachBadgeRow coachProfileBadges">
                {specialties.map(item => (
                  <span className="coachBadge" key={item}>
                    {item}
                  </span>
                ))}
              </div>
            )}

            <RelationshipActions
              viewState={viewState}
              statusLabel={statusLabel}
              saving={saving}
              requestBlocked={requestBlocked}
              onRequest={onRequest}
              onAccept={onAccept}
              onDecline={onDecline}
              onEnd={onEnd}
              onViewRelationship={onViewRelationship}
            />
          </div>
        </section>

        <section className="coachProfileContent">
          <div className="coachProfileInfoCard">
            <h2>About</h2>
            <p>
              {bio ||
                "This career coach has not added a public bio yet."}
            </p>
          </div>

          <div className="coachProfileInfoCard">
            <h2>Contact</h2>
            {contact.email && <p><strong>Email:</strong> {contact.email}</p>}
            {contact.phone && <p><strong>Phone:</strong> {contact.phone}</p>}
            {!contact.email && !contact.phone && (
              <p>Contact details are not publicly listed.</p>
            )}
          </div>

          {relationship && (
            <div className="coachProfileInfoCard">
              <h2>Relationship</h2>
              <p>{statusLabel}</p>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

export default function PublicCareerCoachDirectory() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { userData, role } = useJobBankRole(currentUser);
  const { balance, setBalance } = useContext(TokenContext);

  const syncOptions = useMemo(() => ({
    uid: currentUser?.uid,
    userData: userData || {},
    role: role || ''
  }), [currentUser, userData, role]);

  const [coaches, setCoaches] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [relationship, setRelationship] = useState(null);
  const [activeRelationship, setActiveRelationship] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingRelationship, setSavingRelationship] = useState(false);

  const candidateName = getCoachDisplayName(userData || {});
  const candidateEmail = userData?.email || currentUser?.email || '';

  const viewState = useMemo(
    () => getRelationshipViewState(relationship),
    [relationship]
  );

  const statusLabel = useMemo(
    () => getRelationshipStatusLabel(viewState),
    [viewState]
  );

  const sortedCoaches = useMemo(() => {
    return [...coaches].sort((a, b) =>
      getCoachDisplayName(a).localeCompare(getCoachDisplayName(b))
    );
  }, [coaches]);

  useEffect(() => {
    let mounted = true;

    async function fetchTokens() {
      if (!currentUser?.uid) return;

      try {
        const snap = await getDoc(doc(db, "users", currentUser.uid));
        if (snap.exists() && mounted) {
          setBalance(snap.data().tokens ?? 0);
        }
      } catch (error) {
        console.error("Unable to load token balance:", error);
      }
    }

    fetchTokens();

    return () => {
      mounted = false;
    };
  }, [currentUser, setBalance]);

  const loadRelationshipContext = useCallback(async (coachUid) => {
    if (!currentUser?.uid || !coachUid) {
      setRelationship(null);
      setActiveRelationship(null);
      return;
    }

    await migrateLegacyRelationships({ uid: currentUser.uid });

    const [coachRelationship, active] = await Promise.all([
      getRelationship(currentUser.uid, coachUid),
      getActiveCandidateRelationship(currentUser.uid)
    ]);

    setRelationship(coachRelationship);
    setActiveRelationship(active);
  }, [currentUser]);

  const loadCoaches = useCallback(async () => {
    try {
      setLoading(true);

      if (uid) {
        const coach = await getDirectoryCoach(uid, syncOptions);
        setSelectedCoach(coach);

        if (coach) {
          await loadRelationshipContext(coach.id);
        }

        return;
      }

      const directoryCoaches = await listDirectoryCoaches(syncOptions);
      setCoaches(directoryCoaches);
    } catch (error) {
      console.error("Unable to load career coach profiles:", error);
    } finally {
      setLoading(false);
    }
  }, [uid, loadRelationshipContext, syncOptions]);

  useEffect(() => {
    loadCoaches();
  }, [loadCoaches]);

  async function refreshRelationship() {
    if (selectedCoach?.id) {
      await loadRelationshipContext(selectedCoach.id);
    }
  }

  async function handleRequestCoach() {
    if (!currentUser) {
      return redirectToJobBankLogin(window.location.pathname);
    }

    if (!selectedCoach?.id) return;

    try {
      setSavingRelationship(true);

      await requestCoach({
        candidateUid: currentUser.uid,
        coachUid: selectedCoach.id,
        candidateName,
        candidateEmail,
        coachName: getCoachDisplayName(selectedCoach),
        coachEmail: selectedCoach.email || ""
      });

      await refreshRelationship();
      alert("Career coach request submitted.");
    } catch (error) {
      console.error("Unable to request career coach:", error);
      alert(error.message || "Unable to request career coach.");
    } finally {
      setSavingRelationship(false);
    }
  }

  async function handleAcceptCoach() {
    if (!currentUser || !selectedCoach?.id) return;

    try {
      setSavingRelationship(true);

      await acceptRelationship({
        candidateUid: currentUser.uid,
        coachUid: selectedCoach.id,
        candidateName,
        candidateEmail,
        coachName: getCoachDisplayName(selectedCoach),
        coachEmail: selectedCoach.email || ""
      });

      await refreshRelationship();
      alert("Coaching relationship accepted.");
    } catch (error) {
      console.error("Unable to accept coaching invitation:", error);
      alert(error.message || "Unable to accept invitation.");
    } finally {
      setSavingRelationship(false);
    }
  }

  async function handleDeclineCoach() {
    if (!currentUser || !selectedCoach?.id) return;
    if (!window.confirm("Decline this coaching invitation?")) return;

    try {
      setSavingRelationship(true);

      await declineRelationship({
        candidateUid: currentUser.uid,
        coachUid: selectedCoach.id
      });

      await refreshRelationship();
    } catch (error) {
      console.error("Unable to decline coaching invitation:", error);
      alert("Unable to decline invitation.");
    } finally {
      setSavingRelationship(false);
    }
  }

  async function handleEndRelationship() {
    if (!currentUser || !selectedCoach?.id) return;
    if (!window.confirm("End your active coaching relationship?")) return;

    try {
      setSavingRelationship(true);

      await endRelationship({
        candidateUid: currentUser.uid,
        coachUid: selectedCoach.id
      });

      await refreshRelationship();
    } catch (error) {
      console.error("Unable to end coaching relationship:", error);
      alert(error.message || "Unable to end relationship.");
    } finally {
      setSavingRelationship(false);
    }
  }

  function handleViewRelationship() {
    navigate("/profile");
  }

  const requestBlocked = Boolean(
    activeRelationship &&
    activeRelationship.coachUid !== selectedCoach?.id &&
    viewState === RELATIONSHIP_VIEW_STATE.NONE
  );

  if (loading) {
    return (
      <div className="publicDirectoryPage">
        <div className="publicProfileEmpty">Loading career coaches...</div>
        <Drawer balance={balance} />
      </div>
    );
  }

  if (uid) {
    if (!selectedCoach) {
      return (
        <div className="publicDirectoryPage">
          <div className="publicProfileEmpty">Career coach not found.</div>
          <Drawer balance={balance} />
        </div>
      );
    }

    return (
      <div className="publicDirectoryPage">
        <header className="publicDirectoryHeader">
          <h1>Career Coach Profile</h1>
          <p>
            Review this coach, check your relationship status, and manage your
            coaching connection through the Job Bank.
          </p>
        </header>

        {requestBlocked && (
          <div className="publicProfileEmpty">
            You already have an active career coach. End that relationship from
            your profile before requesting another coach.
          </div>
        )}

        <CoachProfilePanel
          coach={selectedCoach}
          relationship={relationship}
          viewState={viewState}
          statusLabel={statusLabel}
          saving={savingRelationship}
          requestBlocked={requestBlocked}
          onBack={() => navigate("/careercoaches")}
          onRequest={handleRequestCoach}
          onAccept={handleAcceptCoach}
          onDecline={handleDeclineCoach}
          onEnd={handleEndRelationship}
          onViewRelationship={handleViewRelationship}
        />

        <Drawer balance={balance} />
      </div>
    );
  }

  return (
    <div className="publicDirectoryPage">
      <header className="publicDirectoryHeader">
        <h1>Career Coaches</h1>
        <p>
          Connect with experienced career coaches who can help you prepare for
          interviews, strengthen your resume, and advocate with employers in
          your community.
        </p>
      </header>

      {sortedCoaches.length === 0 ? (
        <div className="publicProfileEmpty">
          No career coaches are currently listed.
        </div>
      ) : (
        <div className="publicDirectoryGrid">
          {sortedCoaches.map(coach => (
            <CoachDirectoryCard
              key={coach.id}
              coach={coach}
              onClick={() => navigate(`/careercoaches/${coach.id}`)}
            />
          ))}
        </div>
      )}

      <Drawer balance={balance} />
    </div>
  );
}
